import React, {
  useMemo,
  forwardRef,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
} from "react";
import { Form, Input, TextArea, Button } from "semantic-ui-react";

import ComboBox from "react-lib/apps/common/ComboBox";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import SearchBox from "react-lib/apps/common/SearchBox";
import ModAuthen from "react-lib/apps/common/ModAuthen";
import ErrorMessage from "react-lib/apps/common/ErrorMessage";

import * as Util from "../../utils";
import moment from "moment";

// Config
import CONFIG from "config/config";
import { useIntl } from "react-intl";
import { adToBeWithSetFormat } from "react-lib/utils/dateUtils";

interface SubScannerFormProps {
  scanDocId?: number;
  isEditing?: boolean;
  isMeBilling?: boolean;
  data?: Record<string, any>;
  onGetDocumentTypeOptions?: () => void;
  divisionOptions?: any[];
  onCancelApprove?: () => void;
  cancelDocError?: Record<string, any> | string;
  editDocError?: Record<string, any> | string;
  loading?: boolean;
  onEditApprove?: () => void;
  onEditClicked?: () => void;
  onScanClicked?: () => void;
  cancelEditClicked?: () => void;
  hideButtonCancel?: boolean;
  buttonEditTextTh?: boolean;
  showDocumentCategory?: boolean;
  categoryOptions?: any[];
  onGetSearchOptions?: () => void;
  isLoadingScan?: boolean;
  ownerDivision?: number;
  hideDoctor?: boolean;
  disabledDocCategory?: boolean;
  disabledDocType?: boolean;
  categoryCode?: boolean;
}

const DEFAULT_PROPS = {
  scanDocId: null,
  isEditing: false,
  data: {},
  onGetDocumentTypeOptions: () => [null, null],
  divisionOptions: [],
  onCancelApprove: () => {},
  cancelDocError: null,
  editDocError: null,
  loading: false,
  onEditApprove: () => {},
  onEditClicked: () => {},
  onScanClicked: () => {},
  cancelEditClicked: () => {},
  hideButtonCancel: false,
  buttonEditTextTh: false,
  showDocumentCategory: false,
  categoryOptions: [],
  onGetSearchOptions: () => {},
  isLoadingScan: false,
  hideDoctor: false,
  disabledDocType: false,
};

const SubScannerForm = forwardRef<any, SubScannerFormProps>((props, ref) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [scanDocId, setScanDocId] = useState(null);
  const [documentType, setDocumentType] = useState<any>(null);
  const [documentTypeName, setDocumentTypeName] = useState("");
  const [documentNo, setDocumentNo] = useState("");
  const [version, setVersion] = useState("");
  const [ownerDivision, setOwnerDivision] = useState<number | null>(null);
  const [documentCategory, setDocumentCategory] = useState<any>(null);
  const [documentTypeOptions, setDocumentTypeOptions] = useState(null);
  const [documentDate, setDocumentDate] = useState(Util.formatDate(moment()));
  const [expiryDate, setExpiryDate] = useState("");
  const [remark, setRemark] = useState("");
  const [openModEditAuthen, setOpenModEditAuthen] = useState(false);
  const [openModCancelAuthen, setOpenModCancelAuthen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [noDocTypeError, setNoDocTypeError] = useState<any>(null);
  const [noOwnerDivisionError, setNoOwnerDivisionError] = useState<any>(null);

  const docTypeRef = useRef<any>();
  const doctorRef = useRef<any>();

  useImperativeHandle(ref, () => ({
    clearForm: () => {
      clearForm();
    },
    changeOwnerDivision: (id: number) => {
      setOwnerDivision(id);
    },
    fillRow: () => {
      // @ts-ignore
      return fillRow();
    },
    changeCategory: (id: number) => {
      let documentCategory = props.documentCategoryId || id;
      handleChangeCategory(documentCategory || "");
    },
  }));

  useEffect(() => {
    setOwnerDivision(props.ownerDivision);
  }, [props.ownerDivision]);

  useEffect(() => {
    setIsEditing(props.isEditing);
  }, [props.isEditing]);

  useEffect(() => {
    setScanDocId(props.scanDocId);
  }, [props.scanDocId]);

  useEffect(() => {
    if (props.data && Object.keys(props.data).length > 0) {
      fillFormEdit(props.data);
    }
  }, [props.data]);

  const clearForm = () => {
    docTypeRef.current?.clear();

    setDocumentType(null);
    setDocumentTypeName("");
    setDocumentNo("");
    setVersion("");
    setOwnerDivision(null);
    setDocumentDate(Util.formatDate(moment()));
    setExpiryDate("");
    setRemark("");
    setIsEditing(false);
    setNoDocTypeError(null);
    setNoOwnerDivisionError(null);
    if (props.showDocumentCategory) {
      const defaultCategory = props.categoryCode || "UNDEFINED";
      const notSpecifiedId = props.categoryOptions.find(
        (item: any) => item.code === defaultCategory,
      )?.id;

      if (!props.isViewDocumant) {
        handleChangeCategory(notSpecifiedId || "");
      }
    }
  };

  const fillFormEdit = (row: any) => {
    console.log(" fillFormEdit row: ", row);
    console.log(" fillFormEdit doctorRef: ", doctorRef);
    console.log(" fillFormEdit props.showDocumentCategory", props.showDocumentCategory);
    if (row.id && !row.id.toString().includes("fakeId")) {
      setScanDocId(row.id);
    }
    if (row.doctor_fullname) {
      doctorRef?.current?.setTextByElement(row.doctor_fullname);
    }
    setDocumentType(row.document_type);
    setDocumentTypeName(row.document_type_name || row.document_type_name_en);
    setDocumentNo(row.document_no);
    setVersion(row.version);
    setOwnerDivision(row.owner_division);
    setDocumentDate(row.document_date);
    setExpiryDate(row.expiry_date);
    setRemark(row.remark);
    setIsEditing(true);
    if (props.showDocumentCategory) {
      handleChangeCategory(row.document_category);
    }
  };

  const fillRow = (row: any) => {
    const docTypeName = documentTypeOptions.find((option) => option.value === documentType)?.text;

    return {
      document_type_name: docTypeRef.current?.getText?.() || docTypeName,
      document_type: docTypeRef.current?.getId?.() || documentType,
      document_no: documentNo,
      document_category: documentCategory,
      version: version,
      owner_division: ownerDivision,
      document_date: documentDate,
      expiry_date: expiryDate,
      remark: remark,
    };
  };

  useEffect(() => {
    if (props.defaultDocName) {
      setDocumentType(props.defaultDocName.id ? props.defaultDocName.id : "");
      setDocumentTypeName(props.defaultDocName.name ? props.defaultDocName.name : "");
    }
  }, [props.defaultDocName]);

  const buttonScanContent = useMemo(() => {
    let content = "";

    if (isEditing) {
      content = !props.buttonEditTextTh ? "EDIT" : "แก้ไข";
    } else if (CONFIG.SCAN_VIA_FILE_BROWSE) {
      content = "Browse File";
    } else {
      content = !props.buttonEditTextTh ? "SCAN" : "สแกน";
    }

    return content;
  }, [props.buttonEditTextTh, isEditing]);

  const handleChangeCategory = async (id: any) => {
    setDocumentCategory(id);

    const [optionsData, optionsError] = await props.onGetDocumentTypeOptions({
      searchText: "",
      documentCategory: id,
    });
    const options = optionsData.map((item: any, index: any) => ({
      key: index,
      text: item.name || item.name_en,
      value: item.id,
    }));
    setDocumentTypeOptions(options);
  };

  return (
    <div className="--override-disabled --grey">
      <Form>
        <ModAuthen
          testid="modCancelAuthen"
          open={openModCancelAuthen}
          approveButtonText="ตกลง"
          denyButtonText="ปิด"
          titleColor="red"
          titleName="ยกเลิกเอกสาร"
          onDeny={() => setOpenModCancelAuthen(false)}
          error={props.cancelDocError}
          loading={props.loading}
          onCloseWithDimmerClick={() => setOpenModCancelAuthen(false)}
          onApprove={({ username, password }: any = {}) =>
            props.onCancelApprove({ username, password, cancelNote, scanDocId })
          }
        >
          <Form.Field label="เหตุผลในการยกเลิก" error={props.cancelDocError ? true : false} />
          <Form.Field error={props.cancelDocError ? true : false}>
            <TextArea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} />
          </Form.Field>
        </ModAuthen>
        <ModAuthen
          testid="modEditAuthen"
          open={openModEditAuthen}
          approveButtonText="ตกลง"
          denyButtonText="ปิด"
          titleName="แก้ไขเอกสาร"
          onDeny={() => setOpenModEditAuthen(false)}
          error={props.editDocError}
          onCloseWithDimmerClick={() => setOpenModEditAuthen(false)}
          loading={props.loading}
          onApprove={({ username, password }: any = {}) =>
            props.onEditApprove({
              username,
              password,
              scanDocId,
              documentType: props.showDocumentCategory ? documentType : docTypeRef.current.getId(),
              documentNo,
              version,
              ownerDivision,
              documentDate,
              expiryDate,
              remark,
              documentCategory: documentCategory,
              encounterDoctor: doctorRef.current?.getId(),
            })
          }
        />
        <ErrorMessage error={noDocTypeError || noOwnerDivisionError} />
        {props.isViewDocumant ? (
          <Form.Group>
            <Form.Field width={3}>
              <label>Type</label>
              <Input disabled value={props.selectedEncounter?.type}></Input>
            </Form.Field>
            <Form.Field width={3}>
              <label>EN/AN</label>
              <Input disabled value={props.selectedEncounter?.number}></Input>
            </Form.Field>
            <Form.Field width={4}>
              <label>วันที่รับบริการ</label>
              <DateTextBox
                disabled
                value={adToBeWithSetFormat(props.selectedEncounter?.queue_date)}
              />
            </Form.Field>
            <Form.Field width={6}>
              <label>แพทย์</label>
              <SearchBox
                id="ScanningDocument-SearchBox-Doctor"
                ref={doctorRef}
                defaultId={doctorRef.current?.getId()}
                defaultText={doctorRef.current?.getText()}
                textField="name_code"
                onGetSearchOptions={props.onGetSearchOptions}
                contentStyle={{ padding: 0 }}
                fetchOnFocus={true}
              />
            </Form.Field>
          </Form.Group>
        ) : (
          <Form.Group>
            {!props.showDocumentCategory ? (
              <Form.Field width={9} error={noDocTypeError ? true : false}>
                <label>{intl.formatMessage({ id: "ชื่อเอกสาร" })}</label>
                <SearchBox
                  id="ScanningDocument-SearchBox-DocName"
                  ref={docTypeRef}
                  defaultId={documentType}
                  defaultText={documentTypeName}
                  textField="name"
                  onGetSearchOptions={props.onGetDocumentTypeOptions}
                  fetchOnFocus={true}
                  onSelectOption={() => setNoDocTypeError(null)}
                />
              </Form.Field>
            ) : (
              <Form.Field width={9} error={!!noOwnerDivisionError}>
                <label>{intl.formatMessage({ id: "แผนกเจ้าของเอกสาร" })}</label>
                <ComboBox
                  id="ScanningDocument-ComboBox-Owner"
                  value={ownerDivision}
                  options={props.divisionOptions}
                  onChange={(e: any, { value }: any) => {
                    setOwnerDivision(value);
                  }}
                  fluid
                  search
                  disabled={props.disabledDocCategory}
                  placeholder="ไม่ระบุ"
                />
              </Form.Field>
            )}
            <Form.Field width={4}>
              <label style={{ minWidth: "max-content" }}>
                {intl.formatMessage({ id: "เลขที่เอกสาร" })}
              </label>
              <Input
                id="ScanningDocument-Input-DocNo"
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
              />
            </Form.Field>
            <Form.Field width={3}>
              <label>Version</label>
              <Input
                id="ScanningDocument-Input-Version"
                type="number"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </Form.Field>
          </Form.Group>
        )}

        <Form.Group>
          {!props.showDocumentCategory ? (
            <Form.Field width={16}>
              <label>{intl.formatMessage({ id: "แผนกเจ้าของเอกสาร" })}</label>
              <ComboBox
                id="ScanningDocument-ComboBox-Owner"
                value={ownerDivision}
                options={props.divisionOptions}
                onChange={(e: any, { value }: any) => setOwnerDivision(value)}
                clearable
                fluid
                placeholder="ไม่ระบุ"
              />
            </Form.Field>
          ) : (
            <>
              <Form.Field width={8}>
                <label>{intl.formatMessage({ id: "ประเภทเอกสาร" })}</label>
                <ComboBox
                  id="ScanningDocument-ComboBox-DocCategory"
                  value={documentCategory}
                  options={props.categoryOptions}
                  disabled={props.disabledDocType}
                  search
                  onChange={(e: any, { value }: any) => {
                    setDocumentType("");
                    handleChangeCategory(value);
                  }}
                  fluid
                  placeholder="ไม่ระบุ"
                />
              </Form.Field>
              <Form.Field
                width={8}
                required={props.isViewDocumant}
                error={noDocTypeError ? true : false}
              >
                <label>{intl.formatMessage({ id: "ชื่อเอกสาร" })}</label>
                <ComboBox
                  id="ScanningDocument-ComboBox-DocName"
                  value={documentType}
                  search
                  options={documentTypeOptions}
                  onChange={(e: any, { value }: any) => setDocumentType(value)}
                  fluid
                  placeholder="ไม่ระบุ"
                />
              </Form.Field>
            </>
          )}
        </Form.Group>
        <Form.Group>
          <Form.Field width={8} required={props.isViewDocumant}>
            <label>{intl.formatMessage({ id: "วันที่ของเอกสาร" })}</label>
            <DateTextBox
              id="ScanningDocument-DateTextBox-DocDate"
              value={documentDate}
              onChange={(date: any) => setDocumentDate(date)}
            />
          </Form.Field>
          <Form.Field width={8}>
            <label>{intl.formatMessage({ id: "วันที่หมดอายุของเอกสาร" })}</label>
            <DateTextBox
              id="ScanningDocument-DateTextBox-ExpDate"
              value={expiryDate}
              onChange={(date: any) => setExpiryDate(date)}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group>
          {props.showDocumentCategory && !props.hideDoctor && (
            <Form.Field width={10}>
              <label
                style={{
                  minWidth: "max-content",
                }}
              >
                {props.isMeBilling
                  ? intl.formatMessage({ id: "ผู้ให้บริการ" })
                  : intl.formatMessage({ id: "ทันตแพทย์" })}
              </label>
              <SearchBox
                id="ScanningDocument-SearchBox-Doctor"
                ref={doctorRef}
                defaultId={doctorRef.current?.getId()}
                defaultText={doctorRef.current?.getText()}
                textField="name_code"
                onGetSearchOptions={props.onGetSearchOptions}
                contentStyle={{ padding: 0 }}
                fetchOnFocus={true}
              />
            </Form.Field>
          )}
          <Form.Field width={props.isViewDocumant ? 14 : 8} style={{ padding: 0 }}>
            <label>{intl.formatMessage({ id: "หมายเหตุ" })}</label>
            <TextArea
              id="ScanningDocument-TextArea-Note"
              rows={1}
              value={remark}
              style={{
                minWidth: "max-content",
              }}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Form.Field>
          <Form.Field style={{ flex: 1 }} />
          {!props.hideButtonCancel && (
            <Form.Field>
              <Button
                fluid
                color="red"
                content={scanDocId ? "ยกเลิก" : "ล้างข้อมูล"}
                style={{
                  display: !isEditing ? "none" : null,
                  minWidth: "max-content",
                }}
                onClick={() => {
                  if (scanDocId) {
                    setOpenModCancelAuthen(true);
                  } else {
                    props.cancelEditClicked();
                  }
                }}
              />
            </Form.Field>
          )}

          <Form.Field
            style={{ padding: 0, display: "flex", alignItems: "baseline", marginTop: "18px" }}
          >
            <Button
              testid="btnScan"
              fluid
              color={isEditing ? "orange" : "green"}
              loading={props.isLoadingScan}
              content={buttonScanContent}
              style={{ minWidth: "max-content" }}
              onClick={() => {
                if (docTypeRef.current && !docTypeRef.current.getId()) {
                  return setNoDocTypeError("กรุณาเลือกค่าในช่อง ประเภทเอกสาร");
                }
                if (props.showDocumentCategory && !documentType) {
                  return setNoDocTypeError("กรุณาเลือกค่าในช่อง ชื่อเอกสาร");
                }
                if (props.isViewDocumant && !documentDate) {
                  return setNoDocTypeError("กรุณาเลือกค่าในช่อง วันที่ของเอกสาร");
                }
                if (props.showDocumentCategory && !ownerDivision && !props.isViewDocumant) {
                  setNoDocTypeError(null);

                  return setNoOwnerDivisionError("กรุณาเลือกค่าในช่อง แผนกเจ้าของเอกสาร");
                }
                if (isEditing) {
                  if (scanDocId) {
                    setOpenModEditAuthen(true);
                  } else {
                    props.onEditClicked();
                  }
                } else {
                  props.onScanClicked();
                }
              }}
            />
          </Form.Field>
        </Form.Group>
      </Form>
    </div>
  );
});

SubScannerForm.displayName = "SubScannerForm";

export default React.memo(SubScannerForm);
